import { observer } from 'mobx-react'
import moment from 'moment'
import React, { useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useLocation } from 'react-router'
import avatarIcon from '../../../../assets/images/common/avatar.svg'
import isExpiredAssign from '../../../../assets/images/isExpiredAssign.svg'
import isSuspicious from '../../../../assets/images/isSuspicious.svg'
import newRequestGray from '../../../../assets/images/orderStatuses/newGray.svg'
import newRequest from '../../../../assets/images/orderStatuses/newWhite.svg'
import NewFilters from '../../../../components/NewFilters/NewFilters'
import { requestFilters } from '../../../../helpers/getRequestsFilters'
import {
  isAdmin,
  isAdministration,
  isAdminType2,
  isCallGuyOperator,
  isDealer,
  isOperator,
  isSuperOperator,
} from '../../../../services/AccountService'
import checkboxStore from '../../../../stores/CheckboxStore'
import {
  closeModalFn,
  openModalFn,
} from '../../../../utils/controllers/ModalController'
import { getStyles } from '../../../../utils/utils/commonUtils'
import { getComp } from '../../../../utils/utils/DI'
import paramsFilterStore from '../../../SummaryStatisticsDealers/store/ParamsFilterStore'
import { useRequestsData } from '../../RequestsSecondOperator/Table/hooks/useRequestsData'
import requestFiltersStyles from '../../styles/requestFiltersStyles'
import store from '../RequestsTableOptimized/RequestsTableOptimizedStore'
import RequestFilterChip from './components/RequestFilterChip'

const NewRequestFilters = ({
  totalCollectionLength,
  defaultParams,
  filtersStore,
  isReportCreate,
  selectOrdersStore,
  openAddRequestDrawer,
  reportRequests,
  actionButtonProps,
  filterRowStyles,
  ordersStore,
  isAgreement,
  operatorStore = {},
  reportMonth,
  scrollIsAlreadyLocked,
  downloadRequestsListPDF,
  selectOrderCollection,
  gray,
  clear,
  search,
  isAddRequestsToReportOpen,
  SSDPageParams = [],
  selectedOrders,
  setSelectedCol,
}) => {
  const { data } = useRequestsData(filtersStore)
  const classes = getStyles(requestFiltersStyles)
  const statusStore = getComp('StatusesStore')
  const intl = useIntl()
  const authorsStore = getComp('AuthorsStore')
  const { reasons } = getComp('InstallationReasonStore')
  const sendersStore = getComp('SendersStore')
  const dealersStore = getComp('DealersStore')
  const { canCreateOrder, canCreateLoadedByOperatorOrder } =
    getComp('UserInfoStore')
  const dataCheckingStatuses = statusStore.getDataCheckingStatuses({
    dealerReportsFilters: isReportCreate,
  })
  const sources = getComp('SourcesStore').sources
  const territoryStore = getComp('TerritoryStore')
  const location = useLocation()
  const currentPath = location.pathname

  const canCreateRequests =
    (isAdminType2() && (canCreateOrder || canCreateLoadedByOperatorOrder)) ||
    canCreateOrder

  const filtersData = [
    requestFilters.statuses(intl, currentPath, paramsFilterStore, classes),
    requestFilters.dataCheckingStatus(intl, dataCheckingStatuses),
    [requestFilters.id(intl), requestFilters.phone(intl)],
    [requestFilters.surnamePartial(intl), requestFilters.namePartial(intl)],
    requestFilters.fullName(intl),
    requestFilters.territory(intl),
    requestFilters.district(intl, filtersStore),
    requestFilters.locality(intl, filtersStore),
    requestFilters.address(intl),
    requestFilters.priorities(intl),
    requestFilters.financialSourceIds(intl),
    [requestFilters.from(intl), requestFilters.to(intl)],
    requestFilters.source(intl),
    requestFilters.authorId(intl),
    requestFilters.sender(intl),
    requestFilters.senderGroupIds(intl),
    requestFilters.reason(intl),
    requestFilters.reasonId(intl),
    [
      requestFilters.installedFrom(intl, reportMonth),
      requestFilters.installedTo(intl, reportMonth),
    ],
    requestFilters.dealerIds(intl, classes, avatarIcon),
    requestFilters.equipmentId(intl),
    requestFilters.reportId(intl),
    requestFilters.agreementCheckingStatus(intl, classes),
    requestFilters.preferentialCategory(intl),
    requestFilters.categories(intl),
    requestFilters.hasDuplicates(intl),
    requestFilters.isExpired(intl, isExpiredAssign),
    requestFilters.isSuspicious(intl, isSuspicious),
  ]

  const getFiltersData = useMemo(() => {
    let availableFilters = []

    if (isAgreement && isAdmin()) {
      availableFilters = [
        ['id', 'fullName', 'address', 'from', 'dealerIds', 'equipmentId'],
        [
          'agreementCheckingStatus',
          'preferentialCategory',
          'categories',
          'hasDuplicates',
        ],
      ]
    } else if (isAgreement && isDealer()) {
      availableFilters = [
        [
          'id',
          'fullName',
          'address',
          'priorities',
          'from',
          'equipmentId',
        ].filter((el) => currentPath !== '/requests' || el !== 'priorities'),
        [
          'agreementCheckingStatus',
          'preferentialCategory',
          'categories',
          'hasDuplicates',
        ],
      ]
    } else if (isAdmin()) {
      availableFilters = getAdminFilters(currentPath)
    } else if (isDealer()) {
      availableFilters = getDealerFilters(
        isReportCreate,
        isAddRequestsToReportOpen,
      )
    } else if (isOperator()) {
      availableFilters =
        isSuperOperator() && operatorStore?.selectedTab === 1
          ? getSuperOperatorRequestsFilters()
          : getOperatorFilters(currentPath, operatorStore)
    } else if (isAdministration()) {
      if (operatorStore?.selectedTab === 0) {
        availableFilters = [
          [
            'statuses',
            'id',
            'fullName',
            'address',
            'priorities',
            'financialSourceIds',
          ].filter((el) => currentPath !== '/requests' || el !== 'priorities'),
          ['from', 'reasonId', 'categories', 'hasDuplicates'],
        ]
      }

      if (operatorStore?.selectedTab === 1) {
        availableFilters = availableFilters = getOperatorFilters(
          currentPath,
          operatorStore,
        )
      }
    }

    if (reportRequests) {
      const reportRequestsFilters = [
        'id',
        'fullName',
        'address',
        'from',
        'installedFrom',
        'preferenceCategory',
        'categories',
        'dataCheckingStatus',
      ]
      availableFilters = availableFilters.map((el) =>
        el.filter((filterName) => reportRequestsFilters.includes(filterName)),
      )
    }

    const setFilterData = () =>
      availableFilters.reduce((acc, filtersArr) => {
        const newFilters = filtersArr.map((propName) => {
          return filtersData.find((el) =>
            Array.isArray(el)
              ? el[0].propName === propName
              : el.propName === propName,
          )
        })
        acc.push(newFilters)
        return acc
      }, [])

    return setFilterData()
  }, [reasons, filtersData, sources, operatorStore.selectedTab])

  useEffect(() => {
    return () => {
      if (!location.pathname.startsWith('/reports')) {
        filtersStore.clear()
      }
    }
  }, [])

  function normalizePhoneNumber(phoneNumber) {
    return phoneNumber.replace(/\D/g, '')
  }

  const onSearch = async () => {
    if (!(isOperator() && operatorStore?.selectedTab === 1)) {
      openModalFn['progress-backdrop']()
    }

    const param = {}

    for (const key in filtersStore.data) {
      if (!!filtersStore.data[key]) {
        switch (key) {
          case 'dealerIds':
            if (filtersStore.data[key] === 'none') {
              param.withNoDealer = true
              break
            }
            param[key] = filtersStore.data[key].map(
              (aid) => dealersStore.getDealerBuyAid(aid)?.subjectId,
            )
            break
          case 'from':
          case 'installedFrom':
            param[key] = filtersStore.data[key].format('YYYY-MM-DDTHH:mm:ssZ')
            break
          case 'to':
          case 'installedTo':
            param[key] = moment(filtersStore.data[key])
              .add(1, 'days')
              .format('YYYY-MM-DDTHH:mm:ssZ')
            break
          case 'source':
            param['sourceId'] = filtersStore.data[key]
            break
          case 'sender':
            param['senderId'] = sendersStore.getSenderId(filtersStore.data[key])
            break
          case 'authorId':
            param['authorId'] = authorsStore.getIdByName(filtersStore.data[key])
            break
          case 'id':
            param['orderId'] = filtersStore.data[key]
            break
          case 'phone':
            param['phone'] = normalizePhoneNumber(filtersStore.data[key])
            break
          case 'phoneNumber':
            param['phoneNumber'] = normalizePhoneNumber(filtersStore.data[key])
            break
          default:
            param[key] = filtersStore.data[key]
            break
        }
      }
    }

    if (filtersStore.data.priorities.includes('Без приоритета')) {
      const index = param['priorities'].indexOf('Без приоритета')
      if (index !== -1) {
        param['priorities'][index] = ''
      }
    }

    filtersStore.makeParamCopy({
      ...param,
      ...(defaultParams ? defaultParams : {}),
    })
    if (selectOrdersStore && !isReportCreate) {
      selectOrdersStore.clearSelectOrders()
    }

    const mergeParams = (param, SSDPageParams) => {
      const result = { ...SSDPageParams }

      Object.keys(param).forEach((key) => {
        const value = param[key]
        if (
          (Array.isArray(value) && value.length > 0) ||
          (typeof value === 'number' && value >= 0) ||
          (typeof value === 'boolean' && value) ||
          (typeof value === 'string' && value)
        ) {
          result[key] = value
        }
      })

      return result
    }
    if (search) {
      if (isOperator() && operatorStore.selectedTab === 1) {
        filtersStore.setSelectedFilters()
        filtersStore.makeHighlightTrue()
        return
      }

      await (
        (isOperator() && operatorStore.selectedTab === 0 && operatorStore) ||
        ordersStore
      )
        .reLoad({
          reportId: search,
          params: {
            ...{ ...mergeParams(param, SSDPageParams) },
            size: defaultParams ? 2000 : ordersStore.pageSize,
          },
        })
        .then(() => {
          filtersStore.setSelectedFilters()
          filtersStore.makeHighlightTrue()
        })
        .finally(() => closeModalFn['progress-backdrop']())
    } else if (isAdministration() && operatorStore.selectedTab === 1) {
      await operatorStore
        .reLoad({
          ...param,

          size: operatorStore.pageSize,
        })
        .then(() => {
          filtersStore.setSelectedFilters()
          filtersStore.makeHighlightTrue()
          store.clearSelectOrders()
          setSelectedCol && setSelectedCol('id')
        })
        .finally(() => closeModalFn['progress-backdrop']())
    } else {
      if (isOperator() && operatorStore.selectedTab === 1) {
        filtersStore.setSelectedFilters()
        filtersStore.makeHighlightTrue()
        store.clearSelectOrders()
        setSelectedCol && setSelectedCol('id')
        return
      }

      await (
        (isOperator() && operatorStore.selectedTab === 0 && operatorStore) ||
        ordersStore
      )
        .reLoad({
          ...{
            ...mergeParams(param, SSDPageParams),
            ...defaultParams,
          },
          size: defaultParams ? 2000 : ordersStore.pageSize,
        })
        .then(() => {
          filtersStore.setSelectedFilters()
          filtersStore.makeHighlightTrue()
          store.clearSelectOrders()
          setSelectedCol && setSelectedCol('id')
        })
        .finally(() => closeModalFn['progress-backdrop']())
    }
  }

  const handleDeleteChip = (propName, multiselectValue) => {
    filtersStore.deleteFilter(propName, multiselectValue)

    if (propName === 'territory') {
      filtersStore.deleteFilter('locality')
      filtersStore.deleteFilter('district')
    } else if (propName === 'district') {
      filtersStore.deleteFilter('locality')
    }

    clear ? clear() : onSearch()
  }
  const setChip = (params, withAction) => (
    <RequestFilterChip {...params} onClick={withAction && handleDeleteChip} />
  )

  return (
    <NewFilters
      gray={gray}
      selectOrdersStore={selectOrdersStore}
      selectOrderCollection={selectOrderCollection}
      downloadRequestsListPDF={downloadRequestsListPDF}
      totalCollectionLength={
        isOperator() && operatorStore.selectedTab === 1
          ? data?.totalSize
          : totalCollectionLength
      }
      isAgreement={isAgreement}
      filtersStore={filtersStore}
      onClear={
        clear
          ? () => {
              filtersStore.clear()
              clear()
            }
          : null
      }
      filtersData={getFiltersData}
      onSearch={onSearch}
      setChip={setChip}
      actionButtonProps={
        actionButtonProps ||
        (openAddRequestDrawer && {
          name: intl.formatMessage({
            id: isOperator() ? 'заявка на установку' : 'Создать заявку',
          }),
          onClick: openAddRequestDrawer,
          icon: canCreateRequests ? newRequest : newRequestGray,
          disabled: !canCreateRequests,
        })
      }
      filterRowStyles={filterRowStyles}
      scrollIsAlreadyLocked={scrollIsAlreadyLocked}
      selectedOrders={selectedOrders}
      checkboxStore={checkboxStore}
      disableProgress={isOperator() && operatorStore.selectedTab === 1}
    />
  )
}

const getAdminFilters = (currentPath) => {
  return currentPath === '/summary-statistics-dealers'
    ? [
        [
          'id',
          'statuses',
          'financialSourceIds',
          'reasonId',
          'equipmentId',
        ].filter((el) =>
          paramsFilterStore.statusesPortal.includes(2)
            ? el !== 'equipmentId'
            : true,
        ),
      ]
    : [
        [
          'statuses',
          'dataCheckingStatus',
          'id',
          'surnamePartial',
          'fullName',
          'territory',
          'district',
          'locality',
          'address',
          'priorities',
          'financialSourceIds',
          'from',
          'source',
          'authorId',
        ].filter((el) => currentPath !== '/requests' || el !== 'priorities'),
        [
          'reason',
          'reasonId',
          'sender',
          'SenderGroupIds',
          'installedFrom',
          'dealerIds',
          'equipmentId',
          'preferentialCategory',
          'categories',
          'hasDuplicates',
          'isExpired',
          'isSuspicious',
        ],
      ]
}

const getDealerFilters = (isReportCreate, isAddRequestsToReportOpen) =>
  isReportCreate
    ? isAddRequestsToReportOpen
      ? [
          ['id', 'fullName'],
          ['installedFrom', 'address'],
        ]
      : [
          ['id', 'fullName', 'financialSourceIds'],
          ['SenderGroupIds', 'installedFrom', 'address'],
        ]
    : [
        [
          'statuses',
          'dataCheckingStatus',
          'id',
          'surnamePartial',
          'fullName',
          'territory',
          'district',
          'locality',
          'address',
          'from',
          'financialSourceIds',
          'installedFrom',
        ],
        [
          'reasonId',
          'sender',
          'SenderGroupIds',
          'equipmentId',
          'categories',
          'hasDuplicates',
          'isExpired',
        ],
      ]

const getOperatorFilters = (currentPath, operatorStore) => {
  const baseFilters = [
    'id',
    'fullName',
    'address',
    'priorities',
    'financialSourceIds',
  ].filter((el) => currentPath !== '/requests' || el !== 'priorities')

  if (operatorStore?.selectedTab === 0) {
    return [baseFilters, ['categories', 'hasDuplicates']]
  } else if ([2, 3].includes(operatorStore.selectedTab)) {
    return [[...baseFilters, 'from'], ['hasDuplicates']]
  } else {
    return [
      ['statuses', ...baseFilters],
      isCallGuyOperator()
        ? ['reportId', 'categories', 'hasDuplicates']
        : ['categories', 'hasDuplicates'],
    ]
  }
}

const getSuperOperatorRequestsFilters = () => {
  return [
    [
      'statuses',
      'id',
      'surnamePartial',
      'fullName',
      'territory',
      'district',
      'locality',
      'address',
      'financialSourceIds',
      'from',
      'authorId',
    ],
    [
      'reasonId',
      'sender',
      'SenderGroupIds',
      'installedFrom',
      'dealerIds',
      'reportId',
      'categories',
      'hasDuplicates',
      'isExpired',
      'isSuspicious',
    ],
  ]
}

export default observer(NewRequestFilters)
